import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AogTag, isAog } from "@/components/shipment/AogTag";
import { Plus, Search, Mail, Loader2, Briefcase, Plane, Archive, Trash2, CheckCircle2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { stagesFor, stageLabel, canActStage, SHIPMENT_TYPES, FWD_STATUSES, COMPLETED_ROLES } from "@/lib/stages";
import { MonthlyReport, RowPdf } from "@/components/shipment/CompletedReports";
import { InboxStatus } from "@/components/InboxStatus";
import { SkippedEmails } from "@/components/SkippedEmails";
import { ShipmentAdminActions } from "@/components/shipment/ShipmentAdminActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const stageBadge = (key) => (
  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border bg-slate-800/60 border-slate-700 text-slate-300">
    {stageLabel(key)}
  </span>
);
const FOLDER_ICON = { brokerage: Briefcase, forwarding: Plane, completed: CheckCircle2, archive: Archive };
const fwdStatusText = (d) => (d?.fwd_status === "Other" ? `Other: ${d.fwd_status_other || "—"}` : d?.fwd_status || "");

export default function Shipments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const wanted = params.get("type");
  const canCompleted = COMPLETED_ROLES.includes(user?.role);
  const archived = isAdmin && wanted === "archive";
  const completed = canCompleted && wanted === "completed";
  const type = archived ? "archive" : completed ? "completed" : wanted === "forwarding" ? "forwarding" : "brokerage";
  const stages = stagesFor(type === "forwarding" ? "forwarding" : "brokerage");
  const folders = [...SHIPMENT_TYPES, ...(canCompleted ? [["completed", "Completed"]] : []), ...(isAdmin ? [["archive", "Archive"]] : [])];
  const [list, setList] = useState([]);
  const [archivedList, setArchivedList] = useState([]);
  const [completedList, setCompletedList] = useState([]);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const fwd = type === "forwarding";
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    sender_name: "", sender_email: "", subject: "", body: "", mawb_hint: "", origin_hint: ""
  });

  const load = async () => {
    try {
      const none = Promise.resolve({ data: [] });
      const [a, b, c] = await Promise.all([api.get("/shipments"), isAdmin ? api.get("/shipments?archived=true") : none,
                                           canCompleted ? api.get("/shipments?completed=true") : none]);
      setList(a.data); setArchivedList(b.data); setCompletedList(c.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin, canCompleted]);

  const simulate = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/shipments/pre-alert/simulate", { ...form, shipment_type: type });
      toast.success(`Pre-alert received → ${data.shipment_number}`);
      setOpen(false);
      setForm({ sender_name: "", sender_email: "", subject: "", body: "", mawb_hint: "", origin_hint: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSubmitting(false); }
  };

  const filtered = (archived ? archivedList : completed ? completedList : list).filter((s) => {
    if (!archived && !completed && (s.type || "brokerage") !== type) return false;
    if (stageFilter !== "all" && stages.some((x) => x.key === stageFilter) && s.current_stage !== stageFilter) return false;
    if (fwd && statusFilter !== "all" && (s.documentation?.fwd_status || "none") !== statusFilter) return false;
    if (!q) return true;
    const n = q.toLowerCase();
    return (
      s.shipment_number?.toLowerCase().includes(n) ||
      s.documentation?.mawb_number?.toLowerCase().includes(n) ||
      s.documentation?.consignee?.toLowerCase().includes(n) ||
      s.documentation?.client_name?.toLowerCase().includes(n) ||
      s.pre_alert?.subject?.toLowerCase().includes(n)
    );
  }).sort((a, b) => (archived ? 0 : Number(isAog(b)) - Number(isAog(a))));

  const canSimulate = canActStage(user?.role, "pre_alert");
  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const archiveSelected = async () => {
    if (!window.confirm(`Move ${selected.length} shipment(s) to the Archive? You can restore them later.`)) return;
    const res = await Promise.allSettled(selected.map((id) => api.delete(`/shipments/${id}`)));
    toast.success(`${res.filter((r) => r.status === "fulfilled").length} shipment(s) moved to Archive`);
    setSelected([]); load();
  };
  const purgeArchive = async () => {
    if (!window.confirm(`Permanently delete ALL ${archivedList.length} archived shipment(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/shipments/archive/purge");
      toast.success(`${data.deleted} shipment(s) permanently deleted`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-sky-400 mb-1">Operations</div>
          <h1 className="font-display text-4xl font-bold">Shipments</h1>
          <p className="text-slate-400 text-sm mt-1">{archived ? "Archive: deleted shipments. Restore them or delete them permanently." : completed ? "Completed: shipments acknowledged by billing. Download each report or a monthly summary." : type === "forwarding" ? "Forwarding: pre-alert, documentation and billing transmittal." : "Brokerage: pre-alert through release, delivery and billing transmittal."}</p>
        </div>
        {canSimulate && !archived && !completed && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <InboxStatus onImported={load} />
          {["admin", "broker_manager", "broker_csr"].includes(user?.role) && <SkippedEmails onImported={load} />}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-simulate-prealert" className="bg-blue-600 hover:bg-blue-500 text-white">
                <Mail size={16} className="mr-2" /> Simulate Pre-Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Incoming Pre-Alert · {type === "forwarding" ? "Forwarding" : "Brokerage"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Sender Name</Label>
                    <Input data-testid="input-prealert-sender-name" value={form.sender_name}
                           onChange={(e) => setForm({...form, sender_name: e.target.value})} className="bg-slate-950 border-slate-700" />
                  </div>
                  <div>
                    <Label>Sender Email</Label>
                    <Input data-testid="input-prealert-sender-email" value={form.sender_email}
                           onChange={(e) => setForm({...form, sender_email: e.target.value})} className="bg-slate-950 border-slate-700" />
                  </div>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input data-testid="input-prealert-subject" value={form.subject}
                         onChange={(e) => setForm({...form, subject: e.target.value})} className="bg-slate-950 border-slate-700" />
                </div>
                <div>
                  <Label>Body</Label>
                  <Textarea data-testid="input-prealert-body" rows={5} value={form.body}
                            onChange={(e) => setForm({...form, body: e.target.value})} className="bg-slate-950 border-slate-700" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>MAWB hint</Label>
                    <Input data-testid="input-prealert-mawb" value={form.mawb_hint}
                           onChange={(e) => setForm({...form, mawb_hint: e.target.value})} className="bg-slate-950 border-slate-700" />
                  </div>
                  <div>
                    <Label>Origin hint</Label>
                    <Input data-testid="input-prealert-origin" value={form.origin_hint}
                           onChange={(e) => setForm({...form, origin_hint: e.target.value})} className="bg-slate-950 border-slate-700" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="button-submit-prealert" onClick={simulate} disabled={submitting}
                        className="bg-blue-600 hover:bg-blue-500">
                  {submitting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Plus size={14} className="mr-2" />}
                  Create shipment & notify CSR team
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className={`grid gap-3 grid-cols-2 ${folders.length >= 4 ? "md:grid-cols-4" : folders.length === 3 ? "md:grid-cols-3" : ""}`}>
        {folders.map(([k, l]) => {
          const Icon = FOLDER_ICON[k];
          const count = k === "archive" ? archivedList.length : k === "completed" ? completedList.length : list.filter((s) => (s.type || "brokerage") === k).length;
          return (
            <button key={k} data-testid={`folder-${k}`} onClick={() => { setParams({ type: k }); setStageFilter("all"); setStatusFilter("all"); setSelected([]); }}
                    className={`text-left rounded-lg border p-4 transition-colors ${k === type ? "border-blue-500/50 bg-blue-600/10" : "border-slate-800 bg-slate-900/40 hover:bg-slate-900"}`}>
              <div className={`flex items-center gap-2 font-display font-bold ${k === type ? "text-sky-300" : "text-slate-200"}`}><Icon size={16} /> {l}</div>
              <div className="text-xs text-slate-500 mt-1">{count} shipment{count === 1 ? "" : "s"}</div>
            </button>
          );
        })}
      </div>

      <div className="glass-card rounded-lg p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="input-shipments-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by MAWB, shipment #, client, consignee…"
                 className="pl-9 bg-slate-950 border-slate-700" />
        </div>
        {!completed && (
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger data-testid="select-stage-filter" className="w-full md:w-56 bg-slate-950 border-slate-700">
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            <SelectItem value="all">All stages</SelectItem>
            {stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        )}
        {completed && <MonthlyReport />}
        {fwd && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="select-fwd-status-filter" className="w-full md:w-56 bg-slate-950 border-slate-700">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="all">All statuses</SelectItem>
              {FWD_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              <SelectItem value="none">No status yet</SelectItem>
            </SelectContent>
          </Select>
        )}
        {isAdmin && !archived && selected.length > 0 && (
          <Button data-testid="button-archive-selected" onClick={archiveSelected} className="bg-rose-600 hover:bg-rose-500">
            <Archive size={14} className="mr-2" /> Archive {selected.length} selected
          </Button>
        )}
        {isAdmin && archived && archivedList.length > 0 && (
          <Button data-testid="button-purge-archive" onClick={purgeArchive} className="bg-rose-700 hover:bg-rose-600">
            <Trash2 size={14} className="mr-2" /> Delete all permanently ({archivedList.length})
          </Button>
        )}
      </div>

      <div className="glass-card rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-widest">
            <tr>
              {isAdmin && !archived && (
                <th className="pl-4 py-3 w-8">
                  <input data-testid="select-all-rows" type="checkbox" className="accent-blue-500"
                         checked={filtered.length > 0 && filtered.every((s) => selected.includes(s.id))}
                         onChange={(e) => setSelected(e.target.checked ? filtered.map((s) => s.id) : [])} />
                </th>
              )}
              <th className="text-left px-5 py-3">Shipment #</th>
              <th className="text-left px-5 py-3">Client</th>
              <th className="text-left px-5 py-3">MAWB</th>
              <th className="text-left px-5 py-3">{completed ? "Folder" : "Stage"}</th>
              {fwd && <th className="text-left px-5 py-3">Status</th>}
              <th className="text-left px-5 py-3">{archived ? "Archived" : completed ? "Completed" : "Updated"}</th>
              {isAdmin && <th className="px-5 py-3" />}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-500">{archived ? "The archive is empty." : completed ? "No completed shipments yet. They appear here once billing acknowledges the transmittal." : "No shipments match your filters."}</td></tr>
            )}
            {filtered.map((s) => (
              <tr key={s.id} data-testid={`shipment-row-${s.id}`} className="border-t border-slate-800 hover:bg-slate-900/40 cursor-pointer">
                {isAdmin && !archived && (
                  <td className="pl-4 py-3">
                    <input data-testid={`select-row-${s.id}`} type="checkbox" className="accent-blue-500" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
                  </td>
                )}
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/shipments/${s.id}`} className="font-mono-ops text-sky-300 hover:text-sky-200">
                      {s.shipment_number}
                    </Link>
                    <AogTag s={s} testid={`aog-tag-${s.id}`} />
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-200">{s.documentation?.client_name || s.pre_alert?.client_hint || s.pre_alert?.sender_name || "—"}</td>
                <td className="px-5 py-3 font-mono-ops text-slate-300">{s.documentation?.mawb_number || s.transmittal?.mawb_number || s.pre_alert?.mawb_hint || "—"}</td>
                <td className="px-5 py-3">{completed ? <span className="capitalize text-slate-300 text-xs">{s.type || "brokerage"}</span> : stageBadge(s.current_stage)}</td>
                {fwd && <td data-testid={`fwd-status-${s.id}`} className="px-5 py-3 text-slate-300 text-xs">{fwdStatusText(s.documentation) || "—"}</td>}
                <td className="px-5 py-3 text-slate-400 text-xs">
                  {archived ? <>{new Date(s.archived_at).toLocaleString()}<div className="capitalize">{s.type || "brokerage"} · by {s.archived_by_name}</div></>
                    : completed ? (
                      <div className="flex items-center gap-3">
                        <div>{new Date(s.transmittal?.acknowledgement?.at || s.updated_at).toLocaleString()}<div>by {s.transmittal?.acknowledgement?.by_name || "—"}</div></div>
                        <RowPdf sh={s} />
                      </div>
                    ) : new Date(s.updated_at).toLocaleString()}
                </td>
                {isAdmin && <td className="px-3 py-2 text-right whitespace-nowrap"><ShipmentAdminActions sh={s} archived={archived} onDone={load} /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
